import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { setCurrentStep, resetWizard } from "../../../redux/Commission/commissionStatementSlice";
import StepProviderDates from "./StepProviderDates";
import StepUploadFile from "./StepUploadFile";
import StepColumnMapping from "./StepColumnMapping";
import StepValidateMatch from "./StepValidateMatch";
import StepApplyReceipts from "./StepApplyReceipts";
import SessionBanner from "./SessionBanner";

const NAVY = "#205393";
const GRN = "#2b8a3e";
const T2 = "#475569";
const T3 = "#94a3b8";
const BRD = "#e2e5ea";
const SUR2 = "#f0f0f0";

const STEPS = [
  { label: "Provider & Dates" },
  { label: "Upload File" },
  { label: "Column Mapping" },
  { label: "Validate & Match" },
  { label: "Apply Receipts" },
];

const FileUploadPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentStep = useSelector((s: RootState) => s.commissionStatement.currentStep);
  const providerId = useSelector((s: RootState) => s.commissionStatement.providerId);
  const statementId = useSelector((s: RootState) => s.commissionStatement.statementId);
  const mappingConfirmed = useSelector((s: RootState) => s.commissionStatement.mappingConfirmed);
  const selectedDetailIds = useSelector((s: RootState) => s.commissionStatement.selectedDetailIds);
  const batchResult = useSelector((s: RootState) => s.commissionStatement.batchResult);

  useEffect(() => {
    return () => {
      dispatch(resetWizard());
    };
  }, [dispatch]);

  const isStepUnlocked = (index: number) => {
    if (index === 0) return true;
    if (index === 1) return !!providerId;
    if (index === 2) return !!statementId;
    if (index === 3) return mappingConfirmed;
    if (index === 4) return selectedDetailIds.length > 0 || !!batchResult;
    return false;
  };

  const handleStepClick = (index: number) => {
    if (isStepUnlocked(index)) {
      dispatch(setCurrentStep(index));
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: return <StepProviderDates />;
      case 1: return <StepUploadFile />;
      case 2: return <StepColumnMapping />;
      case 3: return <StepValidateMatch />;
      case 4: return <StepApplyReceipts />;
      default: return <StepProviderDates />;
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100%", background: "#fff" }}>
      {/* Session banner */}
      {currentStep > 0 && <SessionBanner />}

      {/* Step bar — equal-width steps */}
      <div role="tablist" style={{ background: SUR2, borderBottom: `1px solid ${BRD}`, display: "flex", flexShrink: 0 }}>
        {STEPS.map((step, i) => {
          const done = i < currentStep && isStepUnlocked(i);
          const active = i === currentStep;
          const unlocked = isStepUnlocked(i);
          const isLast = i === STEPS.length - 1;
          return (
            <button
              key={i}
              role="tab"
              aria-current={active ? "step" : undefined}
              aria-disabled={!unlocked}
              onClick={() => handleStepClick(i)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "10px 8px",
                cursor: unlocked ? "pointer" : "not-allowed",
                opacity: !unlocked && !active ? 0.5 : 1,
                position: "relative",
                border: "none",
                borderRight: isLast ? "none" : `1px solid ${BRD}`,
                background: active ? "#fff" : "transparent",
                boxShadow: active ? `inset 0 -2px 0 ${NAVY}` : "none",
                font: "inherit",
              }}
            >
              {/* Circle number */}
              <div style={{
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: done ? GRN : active ? NAVY : "#c8cdd5",
                color: done || active ? "#fff" : T3,
                fontSize: "11px",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                {done ? "✓" : i + 1}
              </div>
              {/* Labels */}
              <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                <span style={{ fontSize: "10px", color: active ? "#1e4a8a" : T3, textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>
                  Step {i + 1}
                </span>
                <span style={{ fontSize: "12px", fontWeight: active ? 600 : 500, color: active ? "#0f172a" : done ? T2 : T3 }}>
                  {step.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active step content */}
      <div style={{ padding: "12px 0px 16px", flex: 1, overflowY: "auto" }}>
        {renderStep()}
      </div>
    </div>
  );
};

export default FileUploadPage;
